/**
 * Analytics Events Enums
 *
 * These enums define all the events that should be tracked across
 * all analytics providers (Mixpanel, PostHog, Google Analytics, etc.)
 * in the GrowDash and Growintel applications.
 */

// General Events
export enum GeneralEvents {
  PAGE_VISIT = 'page_visit',
  LOGIN = 'login',
  LOGOUT = 'logout',
  SIGNUP = 'signup',
  FORGOT_PASSWORD = 'forgot_password',
  RESET_PASSWORD = 'reset_password',
  CHANGE_PASSWORD = 'change_password'
}

// Growintel Events
export enum GrowintelEvents {
  SIGNUP_GROWINTEL_LANDING = 'growintel_signup_growintel_landing',
  SIGNUP_GROWINTEL_LOGIN = 'growintel_signup_growintel_login',
  CLICK_OTP_REQUEST = 'growintel_click_otp_request',
  SUCCESS_OTP_VERIFICATION = 'growintel_success_otp_verification',
  FAILURE_OTP_VERIFICATION = 'growintel_failure_otp_verification',
  CLICK_INFORMATION_PAGE = 'growintel_click_information_page',
  CAPTURE_CREDIT_CARD = 'growintel_capture_credit_card',
  CLICK_SUBSCRIPTION = 'growintel_click_subscription',
  LOGIN = 'growintel_login',
  USE_SHOW_INSIGHTS_CUISINE = 'growintel_use_show_insights_cuisine',
  // growintel specific events
  USE_SHOW_INSIGHTS_TRENDS = 'growintel_use_show_insights_trends',
  USE_SEARCH = 'growintel_use_search',
  USE_MENU_ANALYZER = 'growintel_use_menu_analyzer',
  USE_MENU_COMPARISON = 'growintel_use_menu_comparison',
  SUBSCRIPTION_CANCELLED = 'growintel_subscription_cancelled',
  USE_WATCHLIST_ADD = 'growintel_use_watchlist_add',
  // new added events
  SUBSCRIPTION_PAGE_LOGOUT = 'growintel_subscription_page_logout'
}

// Growdash Events
export enum GrowdashEvents {
  USE_FILTER = 'growdash_use_filter',
  USE_ADS_DETAIL = 'growdash_use_ads_detail',
  USE_ADS_CREATE = 'growdash_use_ads_create',
  USE_DISCOUNT_CREATE = 'growdash_use_discount_create',
  USE_ADD_NEW_USER = 'growdash_use_add_new_user',
  SWITCH_CLIENT = 'growdash_switch_client'
}

export enum TestEvents {
  TEST_EVENT = 'test_event'
}

// Union type combining all events
export type AnalyticsEvents =
  | GeneralEvents
  | GrowintelEvents
  | GrowdashEvents
  | TestEvents;

/**
 * Event Properties Interface
 * Defines the structure for event properties that can be passed with events
 */
export interface AnalyticsEventProperties {
  // Common properties
  url?: string;
  page?: string;
  section?: string;
  timestamp?: string;
  user_id?: string;
  user_email?: string;
  user_roles?: string[];

  // Growintel specific properties
  credit_card_detected?: boolean;
  subscription_plan?: string;
  search_query?: string;
  cuisine_type?: string;
  trend_type?: string;
  menu_item?: string;
  comparison_type?: string;

  // Growdash specific properties
  filter_type?: string;
  filter_value?: string;
  filters?: {
    type: string;
    value: string;
  }[];
  ad_type?: string;
  discount_type?: string;
  user_role?: string;
  channel?: string;

  // Custom properties
  [key: string]: any;
}

/**
 * Typed Event Tracking Function
 * Ensures type safety when tracking events
 */
export type TrackAnalyticsEvent = (
  event: AnalyticsEvents,
  properties?: AnalyticsEventProperties
) => void;
